import { ChangeDetectorRef, Directive, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { FiChecklist2ReportType } from '../enums/fi-checklist2-report-type';
import {
    IChecklist2ReportRecentActivity,
    IChecklistV2FilterAvailableItems,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from './fi-checklist2-report';
import { FiChecklist2ReportService } from '../services/fi-checklist2-report.service';
import { AppTextService } from '../../../app-text';
import { FiChecklist2ReportHelperService } from '../services/fi-checklist2-report-helper.service';
import { guid } from '../../../utils/guid';
import { FiChecklist2ReportFilterService } from '../services/fi-checklist2-report-filter.service';
import { FiChecklist2ReportData } from './fi-checklist2-report-data';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { FiChecklist2ReportRecentActivityService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-recent-activity.service';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiReportWidgetDialogsService } from '@fi-shared/fi-report-widget/fi-report-widget-dialogs.service';

@Directive()
export abstract class FiChecklist2ReportWidgetBaseDirective implements OnInit {
    @Input() reportParameter: IChecklistV2ReportParameter;
    @Input() reportData: FiChecklist2ReportData;
    @Input() recentActivity: IChecklist2ReportRecentActivity[];
    @Output() reportParameterDeleted = new EventEmitter<IChecklistV2ReportParameter>();
    @Output() onShowWidgetDetails = new EventEmitter<boolean>();

    get availableItems(): IChecklistV2FilterAvailableItems {
        return this.reportData.availableItems;
    }

    currentFilter: IChecklistV2ReportParameterFilter;

    protected checklist2ReportService: FiChecklist2ReportService;
    protected fiChecklist2ReportFilterService: FiChecklist2ReportFilterService;
    protected appTextService: AppTextService;
    protected fiChecklist2ReportHelperService: FiChecklist2ReportHelperService;
    protected fiChecklist2ReportDataService: FiChecklist2ReportDataService;
    protected cdr: ChangeDetectorRef;
    protected fiChecklist2ReportRecentActivityService: FiChecklist2ReportRecentActivityService;
    protected fiDialogsService: FiDialogsService;
    protected actions: IItemsListAction2<IChecklistV2ReportParameter>[];
    protected fiReportWidgetDialogsService: FiReportWidgetDialogsService;
    constructor(protected injector: Injector) {}

    ngOnInit() {
        this.currentFilter = this.getNewFilterInstance(this.getExistingFilter());
        this.setServices();
        this.setActions();
    }

    protected setServices() {
        this.checklist2ReportService = this.injector.get(FiChecklist2ReportService);
        this.fiChecklist2ReportFilterService = this.injector.get(FiChecklist2ReportFilterService);
        this.appTextService = this.injector.get(AppTextService);
        this.fiChecklist2ReportHelperService = this.injector.get(FiChecklist2ReportHelperService);
        this.fiChecklist2ReportDataService = this.injector.get(FiChecklist2ReportDataService);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiChecklist2ReportRecentActivityService = this.injector.get(FiChecklist2ReportRecentActivityService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.fiReportWidgetDialogsService = this.injector.get(FiReportWidgetDialogsService);
    }

    protected getExistingFilter(): IChecklistV2ReportParameterFilter {
        return this.reportParameter.Settings.Filters?.find((filter: IChecklistV2ReportParameterFilter) => {
            return filter.Guid === this.reportParameter.Settings.CurrentFilterGuid;
        });
    }

    protected getNewFilterInstance(
        existingFilter: IChecklistV2ReportParameterFilter,
    ): IChecklistV2ReportParameterFilter {
        // we should return deep copy of existing filter with new guid and empty name
        return Object.assign({}, existingFilter, { Guid: guid(), Name: '' });
    }

    isChecklistReportTypeEqual(reportType: FiChecklist2ReportType): boolean {
        return this.reportParameter.ReportType === reportType;
    }

    showWidgetDetails(isFilterExpanded: boolean) {
        this.onShowWidgetDetails.emit(isFilterExpanded);
    }

    getWidgetName(): string {
        return this.reportParameter.Name ? this.reportParameter.Name : this.getWidgetNameByType();
    }

    protected getWidgetDescription() {
        return this.fiChecklist2ReportHelperService.getWidgetDescription(this.reportParameter);
    }

    private getWidgetNameByType(): string {
        switch (this.reportParameter.ReportType) {
            case FiChecklist2ReportType.OpenChecklists:
                return this.appTextService.get('checklist.report_open_checklists');

            case FiChecklist2ReportType.AttentionRequired:
                return this.appTextService.get('checklist.report_attention_required');

            case FiChecklist2ReportType.PastChecklists:
                return this.appTextService.get('checklist.checklist_history');

            case FiChecklist2ReportType.RecentActivity:
                return this.appTextService.get('common.recent_activity');
        }
    }

    async deleteReportParameter(): Promise<void> {
        await this.checklist2ReportService.deleteReportParameters([this.reportParameter.Id], this.reportData.auditMode);
        this.reportParameterDeleted.emit(this.reportParameter);
    }

    protected saveReportParameter(): Promise<number> {
        return this.checklist2ReportService.saveReportParameter(this.reportParameter, this.reportData.auditMode);
    }

    protected isChecklistReportWidgetMenuTriggerShown(): boolean {
        return !!this.actions.find((action) => action.condition(this.reportParameter));
    }

    private isCustomReportParameter(): boolean {
        return !!this.reportParameter.Name?.length;
    }

    private setActions(): void {
        this.actions = [
            {
                title: this.appTextService.get('widget.rename_widget'),
                iconClass: 'far fa-pen-line',
                optionType: FiItemsListOptionType.Icon,
                condition: () => this.isCustomReportParameter(),
                onClick: () => this.openRenameWidgetDialog(),
            },
            {
                title: this.appTextService.get('checklist.delete_report_widget'),
                iconClass: 'fa-regular fa-trash',
                optionType: FiItemsListOptionType.Icon,
                condition: () => this.isCustomReportParameter(),
                onClick: () => this.openDeleteWidgetDialog(),
            },
        ];
    }

    protected openRenameWidgetDialog(): void {
        this.fiReportWidgetDialogsService
            .getRenameWidgetDialog(this.reportParameter.Name, this.reportParameter.Description)
            .onYesButtonClick((result) => {
                this.reportParameter.Name = result.widgetName;
                this.reportParameter.Description = result.widgetDescription;
                this.saveReportParameter().then(() => {
                    this.cdr.detectChanges();
                });
            });
    }

    protected openDeleteWidgetDialog(): void {
        this.fiReportWidgetDialogsService.getConfirmWidgetDeleteDialog().onYesButtonClick(() => {
            this.deleteReportParameter();
        });
    }
}
